import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MOCK_SEGMENTS_API_RESPONSE } from "./SegmentData";
import NewTable from "../../../common-components/custom-comps/NewTable";
import { showConfirmAlert } from "../../../components-react/ReactAlert";
import "../../../../styles/trigger-system/ris-segment-listings.css";
import Loader from "../../../ui/Loader";

const RulesChipsPopover = ({ rules }) => {
  const [openUpwards, setOpenUpwards] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const rulesRef = useRef(null);
  const moreChipRef = useRef(null);

  if (!rules || !Array.isArray(rules) || rules.length === 0) {
    return (
      <div className="ris-rules-compact">
        <div className="ris-rules-chips">
          <div className="ris-rule-chip-compact">
            <span className="ris-chip-name">No rules</span>
          </div>
        </div>
      </div>
    );
  }

  const maxVisibleChips = 2;
  const hasMoreRules = rules.length > maxVisibleChips;
  const visibleRules = hasMoreRules ? rules.slice(0, maxVisibleChips) : rules;

  const handleMoreMouseEnter = () => {
    if (moreChipRef.current && rulesRef.current) {
      const rect = moreChipRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const popoverHeight = 220;
      setOpenUpwards(spaceBelow < popoverHeight);
      setShowPopover(true);
    }
  };

  const handleMoreMouseLeave = () => {
    setShowPopover(false);
  };

  return (
    <div
      className="ris-rules-compact"
      ref={rulesRef}
      style={{ position: "relative" }}
    >
      <div className="ris-rules-chips">
        {visibleRules.map((rule, index) => (
          <div
            key={index}
            className="ris-rule-chip-compact"
            title={`${rule.field_name}: ${rule.value}`}
          >
            <span className="ris-chip-name">
              {rule.field_name.replace(/_/g, " ")}
            </span>
            <span className="ris-chip-value">
              {rule.operator}{" "}
              {String(rule.value).length > 15
                ? String(rule.value).substring(0, 15) + "..."
                : String(rule.value)}
            </span>
          </div>
        ))}
        {hasMoreRules && (
          <div
            className="ris-more-rules-chip"
            ref={moreChipRef}
            onMouseEnter={handleMoreMouseEnter}
            onMouseLeave={handleMoreMouseLeave}
          >
            +{rules.length - maxVisibleChips}
          </div>
        )}
      </div>
      {hasMoreRules && showPopover && (
        <div
          className={`ris-rules-expanded${
            openUpwards ? " ris-rules-expanded-up" : ""
          }`}
          style={{
            minWidth: 220,
            maxWidth: 350,
            left: 0,
            right: "auto",
            position: "absolute",
            zIndex: 1000,
          }}
        >
          <div className="ris-expanded-header">
            <span>All Rules ({rules.length})</span>
          </div>
          <div className="ris-expanded-list">
            {rules.map((rule, index) => (
              <div key={index} className="ris-expanded-rule">
                <span className="ris-expanded-name">
                  {rule.field_name.replace(/_/g, " ")}
                </span>
                <span className="ris-expanded-value">
                  {rule.operator} {String(rule.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SegmentListings = ({ openCreateModal, openEditModal }) => {
  const { segmentsData, segmentsLoading, segmentsError } = useSelector(
    (state) => state.transfer
  );

  const iniPTDatas = {
    pagination: [],
    allData: [],
  };
  const [allDatas, setAllDatas] = useState(iniPTDatas);

  // Helper function to determine segment type from filters
  const getSegmentTypeFromFilters = (filters) => {
    if (!filters || !Array.isArray(filters)) return "unknown";

    // Look for specific filters that indicate segment type
    const hasExpiringFilter = filters.some(
      (filter) =>
        filter.field_name === "subscription_expiring_in_days_from" ||
        filter.field_name === "subscription_expiring_in_days_to"
    );

    const hasExpiredFilter = filters.some(
      (filter) => filter.field_name === "expired_days_ago"
    );

    const hasAbandonedFilter = filters.some(
      (filter) =>
        filter.field_name === "abandoned_since_hours" ||
        filter.field_name === "cart_abandoned_date_from" ||
        filter.field_name === "cart_abandoned_date_to"
    );

    if (hasExpiringFilter) return "renewal";
    if (hasExpiredFilter) return "unpaid";
    if (hasAbandonedFilter) return "cart_abandoned";

    return "unknown";
  };

  // Table columns
  const columns = [
    {
      key: "name",
      title: "Segment Name",
      type: "text",
      sortable: true,
      render: (value, row) => (
        <div className="ris-name-cell">
          <strong>{value}</strong>
        </div>
      ),
    },
    {
      key: "filters", // Changed to filters since we'll derive type from it
      title: "Type",
      type: "badge",
      sortable: true,
      render: (value, row) => {
        const segmentType = getSegmentTypeFromFilters(value);
        
        const badgeClass =
          segmentType === "renewal"
            ? "renewal"
            : segmentType === "unpaid"
            ? "unpaid"
            : segmentType === "cart_abandoned"
            ? "cart-abandoned"
            : "ris-type-badge";
        return (
          <span className={`ris-type-badge ${badgeClass}`}>
            {segmentType?.toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "description",
      title: "Description",
      type: "text",
      render: (value) => <div className="ris-description-text">{value}</div>,
    },
    {
      key: "filters",
      title: "Rules",
      type: "custom",
      render: (value) => <RulesChipsPopover rules={value} />,
    },
    {
      key: "status",
      title: "Status",
      type: "status",
      sortable: true,
      render: (value) => (
        <span
          className={
            value === "active"
              ? "ris-status-badge status-active"
              : value === "inactive"
              ? "ris-status-badge status-inactive"
              : "ris-status-badge"
          }
        >
          {value.toUpperCase()}
        </span>
      ),
    },
  ];

  // Actions
  const actions = [
    {
      type: "edit",
      label: "Edit",
      icon: "✏️",
    },
    {
      type: "delete",
      label: "Delete",
      icon: "🗑️",
    },
  ];

  // Filters - Updated to use the derived type
  const filters = [
    {
      key: "status",
      label: "Status",
      options: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
    },
    {
      key: "segment_type", // We'll handle this in the filter logic
      label: "Type",
      options: [
        { value: "renewal", label: "Renewal" },
        { value: "unpaid", label: "Unpaid" },
        { value: "cart_abandoned", label: "Cart Abandoned" },
      ],
    },
  ];

  // Handlers
  const handleEdit = (segment) => {
    if (openEditModal) {
      openEditModal(segment);
    } else {
      toast.info("Edit functionality will be implemented");
    }
  };

  const handleDelete = (segment) => {
    const handleConfirm = () => {
      toast.success(`Segment "${segment.name}" deleted successfully!`);
    };
    showConfirmAlert(
      handleConfirm,
      `Are you sure you want to delete the segment "${segment.name}"?`,
      "Cancel",
      "Delete"
    );
  };

  useEffect(() => {
    if (
      !segmentsLoading &&
      !segmentsError &&
      segmentsData?.status &&
      segmentsData?.data?.length > 0
    ) {
      setAllDatas((prevs) => ({
        ...prevs,
        allData: segmentsData.data,
        pagination:
          segmentsData?.pagination?.length > 0 ? segmentsData.pagination : [],
      }));
    } else {
      setAllDatas(iniPTDatas);
    }
  }, [segmentsLoading, segmentsError, segmentsData]);

  console.log("allDatas", allDatas);

  return (
    <>
      {segmentsLoading ? (
        <Loader />
      ) : (
        <NewTable
          data={allDatas.allData}
          // data={MOCK_SEGMENTS_API_RESPONSE}
          columns={columns}
          actions={actions}
          filters={filters}
          summaryLabel="SEGMENTS"
          onEdit={handleEdit}
          onDelete={handleDelete}
          searchable={true}
          pagination={true}
          defaultPageSize={5}
          pageSizeOptions={[5, 10, 20, 30, 40, 50, 100, 200]}
          striped={true}
          hoverable={true}
          emptyMessage="No segments found matching your criteria."
          className="ris-segments-table-wrapper"
        />
      )}
    </>
  );
};

export default SegmentListings;
